// import AlertsContext from 'common/contexts/alerts';
import useRouter from "common/hooks/use-router";
import useItem from "common/hooks/useItem";
import useList from "common/hooks/useList";
import React, {
  // eslint-disable-next-line comma-dangle
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import fetchJSON from "common/utils/fetchJSON";

import { ListBulletIcon, UsersIcon } from "@heroicons/react/24/outline";
import Header from "common/components/Header/Header";
import { User } from "types";

import FilteredList from "common/components/Table/FilteredList";
import useAuth from "common/hooks/useAuth";
import ToggleSwitch from "common/components/ToggleSwitch/ToggleSwitch";
// import AlertsContext from "common/providers/alerts";
import usePark from "common/hooks/usePark";
import AlertsContext from "common/providers/alerts";
import ManagerSelect from "components/select/UserSelect/ManagerSelect";
import { BsStar, BsStarFill, BsStarHalf } from "react-icons/bs";
import RadioBlock from "common/components/RadioBlock/RadioBlock";
import TabBlockItem from "common/components/TabBlockItem/TabBlockItem";
import UserItem from "./UserItem";

function Users() {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const { setAlert } = useContext(AlertsContext);

  const { user: currentUser } = useAuth();
  const { park } = usePark();
  const canManageUsers =
    (currentUser?.isAdmin ||
      currentUser?.isSuperadmin ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        currentUser?.administrationPermissions?.user?.create)) ??
    false;

  const baseFilters = {
    confirmed: true,
  };

  const {
    fetchItems,
    items,
    isFetching,
    filters,
    setFilter,
    pageCount,
    setPageIndex,
    pageIndex,
    removeListItem,
    itemsCount,
    itemsPerPage,
    error,
  } = useList<User>("users", {
    defaultFilters: park?.id
      ? {
          ...baseFilters,
          "park][id": park?.id,
          "role][name$ne": "Authenticated",
        }
      : { ...baseFilters, "role][name": "Authenticated" },
    defaultSort: "name:ASC",
    populate: ["avatar", "park", "role", "managers"],
    cachePrefix: `Users_${park?.id || "all"}`,
  });

  useEffect(() => {
    if (error) {
      setAlert(error, "danger");
    }
  }, [error, setAlert]);

  useEffect(() => {
    setFilter("park][id", park?.id || false);
  }, [park, setFilter]);

  const { removeItem } = useItem<User>("users", "");

  const handleDelete = useCallback(
    async (id: any) => {
      try {
        removeListItem(id);
        await removeItem(id);
        fetchItems();
      } catch (e: any) {
        setAlert(e, "danger");
        return e;
      }
      return true;
    },
    [removeListItem, fetchItems, removeItem, setAlert]
  );

  const handleNavigate = useCallback(
    (id: any) => {
      navigate(currentUser?.id === id ? "/my-account" : `/users/edit/${id}`);
    },
    [currentUser, navigate]
  );

  const styleTop = "md:w-1/3 xl:flex-1 xl:w-auto";
  const styleBottom = "md:w-1/3 mt-2 xl:flex-1 xl:w-auto xl:mt-0";

  const statusList = useMemo(() => {
    return [
      {
        id: "",
        name: t("status.all"),
        count: null,
        icon: ListBulletIcon,
        color: "secondary",
      },
      {
        id: "Observer",
        name: t("roles.Observer"),
        count: null,
        icon: BsStar,
        color: "#93c5fd",
      },
      {
        id: "Manager",
        name: t("roles.Manager"),
        count: null,
        icon: BsStarHalf,
        color: "#60A5FA",
      },
      {
        id: "Admin",
        name: t("roles.Admin"),
        count: null,
        icon: BsStarFill,
        color: "#1D4ED8",
      },
    ];
  }, [t]);

  const columns = useMemo(
    () => [
      {
        Header: t("common.username"),
        filterName: "username$containsi",
        columnClasses: styleTop,
      },
      {
        Header: t("common.firstname"),
        filterName: "firstname$containsi",
        columnClasses: styleTop,
      },
      {
        Header: t("common.lastname"),
        filterName: "lastname$containsi",
        columnClasses: styleTop,
      },
      {
        Header: t("common.managerLinked"),
        filterName: "managers",
        filterComponent: (props: any) => <ManagerSelect isFilter {...props} />,
        columnClasses: styleTop,
      },
      {
        Header: t("common.job"),
        filterName: "job$containsi",
        columnClasses: styleTop,
      },
      {
        Header: t("forms.confirmed"),
        filterName: "confirmed",
        filterComponent: (props: any) => (
          <div className="pt-2">
            <ToggleSwitch rightLabel={t("forms.confirmed")} {...props} />
          </div>
        ),
        // headerClasses: "mb-3",
        columnClasses: styleBottom,
      },
    ],
    [t]
  ) as any;

  const parkMaxUsers = useMemo(() => park?.maxNbUsers, [park]);

  // Récupérer la limite serveur depuis app-config
  const [serverMaxUsers, setServerMaxUsers] = useState<number | null>(null);
  const [totalPlatformUsers, setTotalPlatformUsers] = useState<number>(0);
  const [unfilteredParkCount, setUnfilteredParkCount] = useState<number | null>(
    null
  );

  useEffect(() => {
    const fetchServerLimitAndUsers = async () => {
      try {
        const res = await fetchJSON({
          url: "app-config",
          method: "GET",
        });

        // Extraire licenseMaxNbUsers de la réponse app-config
        const licenseMax = res?.data?.attributes?.licenseMaxNbUsers;
        setServerMaxUsers(licenseMax || null);

        // Fetch total platform users count
        const usersRes = await fetchJSON({
          url: "users/count",
          method: "GET",
        });
        setTotalPlatformUsers(usersRes || 0);
      } catch (e) {
        console.error("Error fetching server limit:", e);
        setServerMaxUsers(null);
        setTotalPlatformUsers(0);
      }
    };

    fetchServerLimitAndUsers();
  }, []);

  useEffect(() => {
    if (unfilteredParkCount === null && itemsCount > 0) {
      setUnfilteredParkCount(itemsCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsCount]); // unfilteredParkCount retiré des deps, on s'en fiche après le premier set

  const subtitle = useMemo(() => {
    const parkUsersCount = unfilteredParkCount || 0;
    const lines: string[] = [];

    // Park users line
    if (park?.id) {
      if (parkMaxUsers) {
        let parkLine = `${t("titleLegends.nbUsers", {
          nb: parkUsersCount,
          max: parkMaxUsers,
        })} (${t("license.parkLevel")})`;
        if (parkUsersCount >= parkMaxUsers) {
          parkLine += ` - ${t("license.maxNbUsersReached")}`;
        }
        lines.push(parkLine);
      } else {
        lines.push(`${t("titleLegends.parkUsers")}: ${parkUsersCount}`);
      }
    }

    // Platform users line
    if (serverMaxUsers) {
      let platformLine = `${t("titleLegends.nbUsers", {
        nb: totalPlatformUsers,
        max: serverMaxUsers,
      })} (${t("license.serverLevel")})`;
      if (totalPlatformUsers >= serverMaxUsers) {
        platformLine += ` - ${t("license.maxNbUsersReached")}`;
      }
      lines.push(platformLine);
    } else if (currentUser?.isSuperadmin) {
      lines.push(`${t("titleLegends.platformUsers")} : ${totalPlatformUsers}`);
    }

    return lines.join("\n");
  }, [
    unfilteredParkCount,
    park?.id,
    serverMaxUsers,
    currentUser?.isSuperadmin,
    parkMaxUsers,
    t,
    totalPlatformUsers,
  ]);

  return (
    <div className="mx-auto max-w-full py-4 px-4 text-slate-500">
      <Header
        title={t("titlePages.users")}
        subtitle={subtitle}
        btnTitle={t("newItem.user")}
        btnSubtitle={t("newItem.userLegend")}
        Icon={UsersIcon}
        filters={filters}
        setFilter={setFilter}
        addLink="/users/add"
        addLinkDisabled={
          !canManageUsers ||
          !park?.id ||
          // Désactiver si l'une ou l'autre limite est atteinte
          (!!parkMaxUsers && (unfilteredParkCount || 0) >= parkMaxUsers) ||
          (!!serverMaxUsers && (unfilteredParkCount || 0) >= serverMaxUsers)
        }
        isBeforeFilters
        filterName="role][name"
        FilterComponent={(props: any) => (
          <RadioBlock
            Component={TabBlockItem}
            items={statusList}
            horizontal
            noGap
            {...props}
          />
        )}
      />

      <FilteredList
        data={items ?? []}
        columns={columns} // used as filter items
        isFetching={isFetching}
        pageCount={pageCount}
        filters={filters}
        setFilter={setFilter}
        setPageIndex={setPageIndex}
        pageIndex={pageIndex}
        RenderItem={UserItem}
        onDelete={handleDelete}
        onNavigate={handleNavigate}
        itemsCount={itemsCount}
        itemsPerPage={itemsPerPage}
      />
    </div>
  );
}

export default Users;
